/**
 * A user's own density, over the app-wide default an admin sets in
 * Admin → Appearance.
 *
 * `default` (or nothing chosen) follows the admin's setting; `comfortable` or
 * `compact` overrides it for this person. Kept in localStorage per app, like
 * the light/dark Theme switch beside it in the user menu — so it follows the
 * browser, not the person. If people want it to follow them across devices it
 * belongs in a per-user de-hub setting instead (app_support already stores
 * per-user notification prefs that way).
 *
 * Pure apart from the two storage helpers, which never throw. Candidate for
 * promotion to wig-react-library alongside WigThemeProvider.
 */
import { APP_KEY } from "../config/appSupport";

export const DENSITY_PREFERENCES = ["default", "comfortable", "compact"];

const storageKey = () => `wig-density:${APP_KEY}`;

/** Any stored or supplied value, as a preference; unknown is `default`. */
export const normalizeDensityPreference = (value) =>
  DENSITY_PREFERENCES.includes(value) ? value : "default";

/** What the app is drawn with: the user's choice, else the admin's default. */
export const resolveDensity = (preference, appDefault) =>
  normalizeDensityPreference(preference) === "default"
    ? appDefault
    : preference;

export const readDensityPreference = () => {
  try {
    return normalizeDensityPreference(localStorage.getItem(storageKey()));
  } catch {
    return "default";
  }
};

export const writeDensityPreference = (preference) => {
  try {
    const value = normalizeDensityPreference(preference);
    if (value === "default") localStorage.removeItem(storageKey());
    else localStorage.setItem(storageKey(), value);
  } catch {
    // Storage blocked: the choice still holds for this page load.
  }
};
